import React, {Suspense, useEffect} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import {Box, LogoutButton} from '@tenancy.nz/ui';
import Loader from '../../components/Loader';
import MessageFeature from '../message/MessageFeature';
import ToastProvider from '../toast/toast-provider';
import SideNavFeature from '../navigation/side-nav-feature';
import BreadcrumbsFeature from '../navigation/breadcrumbs-feature';
import {BreadcrumbsProvider} from '../navigation/breadcrumbs-context';
import FooterFeature from '../footer/footer-feature';
import Aside from '../../feature-ui/aside';
import PartnerNavList from '../../ui/partner-nav-list';
import Title from '../../ui/title';
import useLogoutMutation from '../../hooks/useLogoutMutation';
import {setAsideCollapsed, setAsideShow} from '../navigation/slice';
import useRouteTitle from '../../hooks/use-route-title';
import {setCookie} from '../../utils/helpers/cookie';
import {SLUG_HOME} from '../../utils/constants/url';

const NO_FOOTER_PAGES = [SLUG_HOME];

function AsideLayoutFeature() {
  const dispatch = useDispatch();
  const title = useRouteTitle();
  const {pathname} = useLocation();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
  const message = useSelector(state => state.message);
  const {
    asideCollapsed,
    asideShow,
    open: mobileOpen,
  } = useSelector(state => state.navigation);

  const [handleLogout] = useLogoutMutation();

  const showFooter = !NO_FOOTER_PAGES.includes(pathname);
  const collapsed = asideCollapsed && !asideShow;

  useEffect(() => {
    if (!isMobile) {
      // eslint-disable-next-line fp/no-mutation
      document.body.style.overflow = '';
    }
    dispatch(setAsideShow(!isMobile && !asideCollapsed));
  }, [dispatch, isMobile, asideCollapsed]);

  const handleAsideHover = show => {
    if (!asideCollapsed) {
      return;
    }
    dispatch(setAsideShow(show));
  };

  const handleToggleCollapsed = () => {
    const next = !asideCollapsed;
    setCookie('collapsed', next);
    dispatch(setAsideCollapsed(next));
    if (!next) {
      dispatch(setAsideShow(true));
    }
  };

  return (
    <BreadcrumbsProvider>
      <ToastProvider>
        <Stack
          flex={1}
          direction={{sm: 'row'}}
          position="relative"
          sx={theme => ({
            [theme.breakpoints.up('md')]: {
              '--content-left': collapsed ? '68px' : '240px',
            },
          })}>
          {title && <Title text={title} />}
          <Aside
            collapsed={isMobile ? !mobileOpen : collapsed}
            switchOn={!asideCollapsed}
            isMobile={isMobile}
            onHover={handleAsideHover}
            onToggleCollapsed={handleToggleCollapsed}>
            <SideNavFeature />
            <PartnerNavList collapsed={collapsed} />
            {isMobile && (
              <LogoutButton
                size="large"
                variant="outlined"
                onClick={() => handleLogout()}
              />
            )}
          </Aside>
          <Stack
            component="section"
            sx={{
              zIndex: 2,
              flex: 1,
              bgcolor: '#ffffff',
              overflow: 'hidden',
              '@supports (overflow: clip)': {overflow: 'clip'},
              minWidth: 0,
              borderRadius: {xs: 0, md: '24px 0 0 0'},
              boxShadow: {md: '0 0 12px 0 rgba(0, 0, 0, 0.20)'},
              mt: {xs: 0, md: 2},
              '&:has([data-floating-panel-pinned])': {pb: '80px'},
            }}>
            {message.show && !message.flash ? (
              <Box px={{xs: 4, md: 8}} mt={{xs: 4, md: 8}}>
                <MessageFeature />
              </Box>
            ) : (
              <MessageFeature />
            )}
            <BreadcrumbsFeature />
            <Suspense
              fallback={
                <Stack
                  flex={1}
                  alignItems="center"
                  justifyContent="center"
                  py={10}>
                  <Loader />
                </Stack>
              }>
              <Outlet />
            </Suspense>
            {showFooter && <FooterFeature />}
          </Stack>
        </Stack>
      </ToastProvider>
    </BreadcrumbsProvider>
  );
}

export default AsideLayoutFeature;
